import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Menu } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useAuth } from "../context/AuthContext";
import api from "../api";

export function useNotifications() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  return { ...data, load };
}

export default function TopHeader({ title, subtitle, onMenuClick }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { items, unread, load } = useNotifications();
  const [open, setOpen] = useState(false);
  const initials = ((user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")).toUpperCase() || "U";

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    load();
    setOpen(false);
    if (n.link) navigate(n.link);
  };
  const readAll = async () => { await api.post("/notifications/read-all").catch(() => {}); load(); };

  return (
    <header className="sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-slate-200 bg-background/80 px-5 py-4 backdrop-blur md:px-8">
      <div className="flex items-center gap-3">
        <button onClick={onMenuClick} data-testid="mobile-menu-button" className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 md:hidden">
          <Menu className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-lg font-bold text-slate-900 md:text-xl">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
          <PopoverTrigger asChild>
            <button data-testid="notifications-bell" className="relative rounded-full border border-slate-200 bg-white p-2 text-slate-600 transition-colors hover:bg-slate-50">
              <Bell className="h-4 w-4" />
              {unread > 0 && (
                <span data-testid="notifications-unread-count" className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-violet-600 px-1 text-[10px] font-bold text-white ring-2 ring-white">{unread}</span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <span className="text-sm font-semibold text-slate-900">Notifications</span>
              {unread > 0 && <button onClick={readAll} data-testid="notifications-read-all" className="text-xs font-medium text-violet-600 hover:underline">Mark all read</button>}
            </div>
            <div className="max-h-72 overflow-auto">
              {items.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-400">No notifications yet.</div>}
              {items.map((n) => (
                <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-${n.id}`} className={`block w-full px-4 py-3 text-left hover:bg-slate-50 ${n.read ? "" : "bg-violet-50/50"}`}>
                  <div className="text-sm font-medium text-slate-800">{n.title}</div>
                  {n.body && <div className="text-xs text-slate-500">{n.body}</div>}
                  <div className="mt-0.5 text-[10px] text-slate-400">{new Date(n.created_at).toLocaleString()}</div>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <button onClick={() => navigate(user?.role?.startsWith("client") ? "/settings" : "/profile")} data-testid="header-avatar" className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-700">
          {initials}
        </button>
      </div>
    </header>
  );
}
